// Line icons drawn exactly as in the mockups (17-unit viewBox, 1.5 stroke,
// round caps) — phone_lib.js tab bar + StatesEmpty icon wells.
import React from 'react';

const PATHS = {
  portfolio: <><path d="M3 13h11" /><path d="M5 13V8M8.5 13V4M12 13V9" /></>,
  autotrader: <><rect x="3" y="5" width="11" height="8" rx="2" /><path d="M8.5 2.5V5" /></>,
  bot: <><rect x="3" y="5" width="11" height="8" rx="2" /><path d="M8.5 2.5V5" /><circle cx="6.5" cy="9" r=".8" /><circle cx="10.5" cy="9" r=".8" /></>,
  scanners: <><circle cx="8.5" cy="8.5" r="5.5" /><circle cx="8.5" cy="8.5" r="2" /></>,
  scanner: <><circle cx="8.5" cy="8.5" r="5.5" /><circle cx="8.5" cy="8.5" r="2" /><path d="M8.5 8.5l4-3" /></>,
  research: <><circle cx="7.5" cy="7.5" r="4.5" /><path d="M11 11l3.5 3.5" /></>,
  search: <><circle cx="7.5" cy="7.5" r="4.5" /><path d="M11 11l3.5 3.5" /></>,
  more: <><circle cx="4" cy="8.5" r="1" /><circle cx="8.5" cy="8.5" r="1" /><circle cx="13" cy="8.5" r="1" /></>,
  link: <><path d="M7 10l3-3" /><path d="M9 4.5l1-1a2.5 2.5 0 0 1 3.5 3.5l-1 1" /><path d="M8 12.5l-1 1A2.5 2.5 0 0 1 3.5 10l1-1" /></>,
  chart: <path d="M3 13l3-4 3 2 5-7" />,
  doc: <><path d="M4 3h6l3 3v8H4z" /><path d="M10 3v3h3" /></>,
  star: <path d="M8.5 2.5l1.8 3.7 4 .6-2.9 2.8.7 4-3.6-1.9-3.6 1.9.7-4L2.7 6.8l4-.6z" />,
  // Undrawn (More sheet / user menu) — same grid and stroke as the tab icons.
  watchlist: <path d="M8.5 2.5l1.8 3.7 4 .6-2.9 2.8.7 4-3.6-1.9-3.6 1.9.7-4L2.7 6.8l4-.6z" />,
  data: <><ellipse cx="8.5" cy="4.5" rx="5" ry="2" /><path d="M3.5 4.5v8c0 1.1 2.2 2 5 2s5-.9 5-2v-8" /><path d="M3.5 8.5c0 1.1 2.2 2 5 2s5-.9 5-2" /></>,
  settings: <><circle cx="8.5" cy="8.5" r="2" /><path d="M8.5 2.5v1.8M8.5 12.7v1.8M2.5 8.5h1.8M12.7 8.5h1.8M4.3 4.3l1.3 1.3M11.4 11.4l1.3 1.3M4.3 12.7l1.3-1.3M11.4 5.6l1.3-1.3" /></>,
  billing: <><rect x="2.5" y="4.5" width="12" height="8" rx="1.5" /><path d="M2.5 7.5h12" /><path d="M5 10.5h2.5" /></>,
  about: <><circle cx="8.5" cy="8.5" r="6" /><path d="M8.5 8v4" /><circle cx="8.5" cy="5.6" r=".5" /></>,
  terms: <><path d="M4 3h6l3 3v8H4z" /><path d="M6.5 8.5h4M6.5 11h4" /></>,
  privacy: <><path d="M8.5 2.5l5 2v4c0 3-2.2 5.2-5 6-2.8-.8-5-3-5-6v-4z" /></>,
  signout: <><path d="M7 3.5H4.5v10H7" /><path d="M10 5.5l3 3-3 3" /><path d="M13 8.5H6.5" /></>,
  halt: <><circle cx="8.5" cy="8.5" r="6" /><path d="M6.5 6.5v4M10.5 6.5v4" /></>,
};

/** <Icon name="research" size={20} /> — stroke follows currentColor. */
export function Icon({ name, size = 17, strokeWidth = 1.5, title, style, className }) {
  const body = PATHS[name];
  if (!body) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 17 17"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinejoin="round"
      strokeLinecap="round"
      aria-hidden={title ? undefined : 'true'}
      role={title ? 'img' : undefined}
      style={style}
      className={className}
    >
      {title ? <title>{title}</title> : null}
      {body}
    </svg>
  );
}

export const ICON_NAMES = Object.keys(PATHS);

/** The 10 px chevron used by .adv / .in selects. */
export const Chevron = ({ size = 10, style }) => (
  <svg width={size} height={size} viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" style={style}>
    <path d="M2 4l3 3 3-3" />
  </svg>
);

/** The phone header back arrow. */
export const BackArrow = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <path d="M12 4l-6 6 6 6" />
  </svg>
);

/** The "+" used on primary buttons ("New rule", phone header plus). */
export const Plus = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <path d="M7 2v10M2 7h10" />
  </svg>
);

export const Close = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
    <path d="M3 3l8 8M11 3l-8 8" />
  </svg>
);

export const Tick = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2.5 6.2l2.3 2.3 4.7-5" />
  </svg>
);

export default Icon;
